import { useMemo, useState } from 'react';
import type { SystemOptionCard as CardData, ScopeItem } from '../types';
import { SystemOptionCard, type SelectionRole } from './SystemOptionCard';

interface Props {
  cards: CardData[];
  /** User-selected option id */
  selectedOptionId: string | null;
  /** User-selected option B id */
  selectedOptionIdB?: string | null;
  /** User-selected option C id */
  selectedOptionIdC?: string | null;
  onSelect: (id: string) => void;
  onSelectB?: (id: string) => void;
  onSelectC?: (id: string) => void;
  /** Premium add-ons for selected option only */
  selectedOptionAddOns?: ScopeItem[];
  onToggleAddOn?: (optionId: string, addOnId: string) => void;
  onNavigateSource?: (sourcePath: string) => void;
}

const normalizeOptionId = (cardId: string): string => cardId.replace(/_/g, '-');

/** Group label and description for each heat source section */
const HEAT_SOURCE_GROUPS: Record<string, { label: string; description: string }> = {
  'gas-combi': {
    label: 'Gas — Combi (no cylinder)',
    description: 'On-demand hot water, no storage cylinder required',
  },
  'gas-stored-standard': {
    label: 'Gas — Stored DHW, Standard Cylinder',
    description: 'Hot water cylinder with modern factory insulation',
  },
  'gas-stored-mixergy': {
    label: 'Gas — Stored DHW, Mixergy Smart Cylinder',
    description: 'Stored-DHW route with smart controls and lower standing losses where that efficiency delta matters',
  },
  'hp-stored-standard': {
    label: 'Heat Pump — Standard Cylinder',
    description: 'Renewable heating with stored hot water',
  },
  'hp-stored-mixergy': {
    label: 'Heat Pump — Mixergy Smart Cylinder',
    description: 'Renewable + stored-DHW route with smart optimisation when house and usage support it',
  },
};

/**
 * Determine which group an option card belongs to.
 * Groups follow: Heat Source → DHW Architecture → Cylinder Class
 */
function getCardGroup(cardId: string): string {
  const normalizedId = normalizeOptionId(cardId);
  const isHP = normalizedId.startsWith('ashp') || normalizedId.startsWith('gshp');
  const isCombi = normalizedId === 'combi' || normalizedId === 'storage-combi';
  const isMixergy = normalizedId.includes('mixergy');

  if (isCombi) return 'gas-combi';
  if (isHP && isMixergy) return 'hp-stored-mixergy';
  if (isHP) return 'hp-stored-standard';
  if (isMixergy) return 'gas-stored-mixergy';
  return 'gas-stored-standard';
}

function cardDisplayScore(card: CardData): number {
  const score = card.scoreBreakdown?.overallScore ?? card.suitabilityScore;
  return typeof score === 'number' && Number.isFinite(score) ? score : 0;
}

export function SystemOptionsArea({ cards, selectedOptionId, selectedOptionIdB, selectedOptionIdC, onSelect, onSelectB, onSelectC, selectedOptionAddOns, onToggleAddOn, onNavigateSource }: Props) {
  const [selectedHeatSource, setSelectedHeatSource] = useState<'all' | HeatSourceFilter>('all');
  const [selectedHotWater, setSelectedHotWater] = useState<'all' | HotWaterFilter>('all');

  if (cards.length === 0) {
    return (
      <div className="hub-options-area">
        <p className="hub-section-label">System options</p>
        <div className="hub-note">
          Fill in the tiles above to generate system evaluations.
        </div>
      </div>
    );
  }

  const getSelectionRole = (id: string): SelectionRole => {
    if (id === selectedOptionId) return 'A';
    if (id === selectedOptionIdB) return 'B';
    if (id === selectedOptionIdC) return 'C';
    return null;
  };

  const getAddOns = (id: string): ScopeItem[] | undefined => (
    id === selectedOptionId ? selectedOptionAddOns : undefined
  );

  const selectionCount = [selectedOptionId, selectedOptionIdB, selectedOptionIdC].filter(Boolean).length;

  const filteredCards = useMemo(() => {
    return cards
      .filter((card) => {
        const heat = getHeatSourceForCard(card.id);
        const water = getHotWaterForCard(card.id);
        const compatibleWater = getCompatibleHotWaterOptions(heat);

        if (selectedHeatSource !== 'all' && heat !== selectedHeatSource) return false;
        if (selectedHotWater !== 'all' && water !== selectedHotWater) return false;

        // Guard for incompatible selections (e.g., forcing "open vented" with combi)
        if (selectedHeatSource !== 'all' && selectedHotWater !== 'all') {
          return compatibleWater.includes(selectedHotWater);
        }

        return true;
      })
      // Keep engine score intact: filters are view controls, not a score normaliser.
      .sort((a, b) => cardDisplayScore(b) - cardDisplayScore(a));
  }, [cards, selectedHeatSource, selectedHotWater]);

  const compatibleHotWaterOptions = selectedHeatSource === 'all'
    ? HOT_WATER_FILTER_OPTIONS
    : HOT_WATER_FILTER_OPTIONS.filter((option) => getCompatibleHotWaterOptions(selectedHeatSource).includes(option.value));

  // Group cards by heat source → DHW architecture → cylinder class
  const groupOrder = ['gas-combi', 'gas-stored-standard', 'gas-stored-mixergy', 'hp-stored-standard', 'hp-stored-mixergy'];
  const grouped = new Map<string, CardData[]>();
  for (const group of groupOrder) grouped.set(group, []);

  for (const card of filteredCards) {
    const group = getCardGroup(card.id);
    const list = grouped.get(group);
    if (list) list.push(card);
  }

  return (
    <div className="hub-options-area">
      <p className="hub-section-label">
        For your home: system options — Heat Source → Hot Water Type → Cylinder Class
        {selectionCount > 0 && (
          <span className="hub-section-label__count">
            {' '}({selectionCount}/3 selected)
          </span>
        )}
      </p>

      <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Heat source</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <FilterChip
              label="All heat sources"
              isActive={selectedHeatSource === 'all'}
              onClick={() => setSelectedHeatSource('all')}
            />
            {HEAT_SOURCE_FILTER_OPTIONS.map((option) => (
              <FilterChip
                key={option.value}
                label={option.label}
                isActive={selectedHeatSource === option.value}
                onClick={() => {
                  setSelectedHeatSource(option.value);
                  if (selectedHotWater !== 'all' && !getCompatibleHotWaterOptions(option.value).includes(selectedHotWater)) {
                    setSelectedHotWater('all');
                  }
                }}
              />
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Hot water option</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <FilterChip
              label="All hot water options"
              isActive={selectedHotWater === 'all'}
              onClick={() => setSelectedHotWater('all')}
            />
            {HOT_WATER_FILTER_OPTIONS.map((option) => {
              const isCompatible = compatibleHotWaterOptions.some((compatible) => compatible.value === option.value);
              return (
                <FilterChip
                  key={option.value}
                  label={option.label}
                  isActive={selectedHotWater === option.value}
                  isDisabled={!isCompatible}
                  onClick={() => setSelectedHotWater(option.value)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {groupOrder.map(groupKey => {
        const groupCards = grouped.get(groupKey) ?? [];
        if (groupCards.length === 0) return null;

        const meta = HEAT_SOURCE_GROUPS[groupKey];
        const isMixergyGroup = groupKey.includes('mixergy');

        return (
          <div key={groupKey} className="hub-options-group" style={{
            marginBottom: 24,
            padding: '12px 16px',
            borderRadius: 8,
            border: isMixergyGroup ? '2px solid #22c55e' : '1px solid #e5e7eb',
            background: isMixergyGroup ? '#f0fdf4' : undefined,
          }}>
            <div style={{ marginBottom: 8 }}>
              <p style={{
                fontWeight: 700,
                fontSize: 15,
                margin: '0 0 2px 0',
                color: isMixergyGroup ? '#16a34a' : '#111827',
              }}>
                {isMixergyGroup ? '⚡ ' : ''}{meta?.label ?? groupKey}
              </p>
              {meta?.description && (
                <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                  {meta.description}
                </p>
              )}
            </div>
            <div className="hub-options-grid">
              {groupCards.map(card => (
                <SystemOptionCard
                  key={card.id}
                  card={card}
                  selectionRole={getSelectionRole(card.id)}
                  onSelect={onSelect}
                  onSelectB={onSelectB}
                  onSelectC={onSelectC}
                  isSelectedOption={card.id === selectedOptionId}
                  isSelectedOptionB={card.id === selectedOptionIdB}
                  isSelectedOptionC={card.id === selectedOptionIdC}
                  addOns={getAddOns(card.id)}
                  onToggleAddOn={onToggleAddOn}
                  onNavigateSource={onNavigateSource}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

type HeatSourceFilter = 'regular' | 'system' | 'combi' | 'storage_combi' | 'ashp' | 'gshp';
type HotWaterFilter = 'open_vented' | 'mixergy_open' | 'unvented' | 'mixergy_unvented' | 'heat_pump_unvented' | 'on_demand' | 'storage_on_demand';

const HEAT_SOURCE_FILTER_OPTIONS: Array<{ value: HeatSourceFilter; label: string }> = [
  { value: 'regular', label: 'Regular' },
  { value: 'system', label: 'System' },
  { value: 'combi', label: 'Combi' },
  { value: 'storage_combi', label: 'Storage combi' },
  { value: 'ashp', label: 'ASHP' },
  { value: 'gshp', label: 'GSHP' },
];

const HOT_WATER_FILTER_OPTIONS: Array<{ value: HotWaterFilter; label: string }> = [
  { value: 'open_vented', label: 'Open vented' },
  { value: 'mixergy_open', label: 'Mixergy open vented' },
  { value: 'unvented', label: 'Unvented' },
  { value: 'mixergy_unvented', label: 'Mixergy unvented' },
  { value: 'heat_pump_unvented', label: 'Heat pump unvented' },
  { value: 'on_demand', label: 'On demand' },
  { value: 'storage_on_demand', label: 'Storage on demand' },
];

function getHeatSourceForCard(cardId: string): HeatSourceFilter {
  const normalizedId = normalizeOptionId(cardId);
  if (normalizedId.startsWith('regular')) return 'regular';
  if (normalizedId.startsWith('system')) return 'system';
  if (normalizedId === 'combi') return 'combi';
  if (normalizedId === 'storage-combi') return 'storage_combi';
  if (normalizedId.startsWith('gshp')) return 'gshp';
  return 'ashp';
}

function getHotWaterForCard(cardId: string): HotWaterFilter {
  const normalizedId = normalizeOptionId(cardId);
  if (normalizedId === 'combi') return 'on_demand';
  if (normalizedId === 'storage-combi') return 'storage_on_demand';
  if (normalizedId.startsWith('ashp') || normalizedId.startsWith('gshp')) {
    if (normalizedId.includes('mixergy')) return 'mixergy_unvented';
    return 'heat_pump_unvented';
  }
  if (normalizedId.includes('mixergy') && normalizedId.includes('open')) return 'mixergy_open';
  if (normalizedId.includes('mixergy')) return 'mixergy_unvented';
  if (normalizedId.includes('open-vented')) return 'open_vented';
  return 'unvented';
}

function getCompatibleHotWaterOptions(heatSource: HeatSourceFilter): HotWaterFilter[] {
  switch (heatSource) {
    case 'regular':
    case 'system':
      return ['open_vented', 'mixergy_open', 'unvented', 'mixergy_unvented'];
    case 'combi':
      return ['on_demand'];
    case 'storage_combi':
      return ['storage_on_demand'];
    case 'ashp':
    case 'gshp':
      return ['heat_pump_unvented', 'mixergy_unvented'];
  }
}

interface FilterChipProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  isDisabled?: boolean;
}

function FilterChip({ label, isActive, onClick, isDisabled = false }: FilterChipProps) {
  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={onClick}
      style={{
        borderRadius: 999,
        padding: '4px 10px',
        border: `1px solid ${isActive ? '#6366f1' : '#d1d5db'}`,
        background: isActive ? '#eef2ff' : '#fff',
        color: isDisabled ? '#9ca3af' : '#111827',
        opacity: isDisabled ? 0.55 : 1,
        fontSize: 12,
        fontWeight: 600,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}
